import { loadVimeoApi } from './vimeo-api.js';

/*
  Elementor background videos (Vimeo embeds and self-hosted files).

  Two problems are handled here.

  1) Preload artifacts in the 3D avatar blocks.
     The iframe is hidden by CSS at first and the fallback image stays
     visible. The iframe fades in only once Vimeo reports real playback.

  2) Dead videos after a long visit.
     Elementor starts every background video at page load, no matter how
     far down the page it sits. A video that has been looping off-screen
     for minutes gets its decoder suspended by mobile Chrome, and because
     Elementor embeds Vimeo with `transparent: true`, the suspended iframe
     just lets the fallback image show through. Scrolling down after a
     while then shows a still picture instead of the video.
     Fix: drive playback from the viewport. Pause what is far away, and
     play it again when it comes back into view, after a tab switch, or
     when playback is detected to have stalled.
*/

var revealBlockSelector = '.vimeo-video-3d-avatar-bg';
var videoContainerSelector = '.elementor-background-video-container';

/*
  How far outside the viewport a video is still kept playing, so it is
  already running by the time it scrolls into view.
*/
var preloadMargin = '300px 0px';
var stallCheckInterval = 3000;

/*
  How long to wait after a block scrolls into view before assuming
  autoplay was refused (iOS Low Power Mode, or another autoplay policy) and
  offering a manual play button instead of leaving a static, seemingly
  broken block on screen.
*/
var tapFallbackDelay = 2500;

/*
  One record per background video, keyed by the Elementor element that
  wraps it. That element is what we observe, since the video container
  itself is absolutely positioned inside it.
*/
var videos = new Map();
var seenContainers = new WeakSet();
var viewportObserver = null;
var stallTimer = null;

function showVideo(block) {
    block.classList.add('video-is-playing');

    var tapButton = block.querySelector('.vimeo-video-3d-avatar-bg__tap-play');

    if (tapButton) {
        tapButton.remove();
    }
}

/*
  Reveal logic for the 3D avatar blocks only. Other background videos
  are not hidden by CSS, so they need nothing here.
*/
function bindReveal(record) {
    var block = record.container.closest(revealBlockSelector);

    if (!block || record.type !== 'vimeo') {
        return;
    }

    record.player.on('play', function () {
        showVideo(block);
    });

    record.player.on('playing', function () {
        showVideo(block);
    });

    /*
      timeupdate keeps firing for as long as the video runs, so it also
      covers the case where autoplay started before we attached.
    */
    record.player.on('timeupdate', function (data) {
        if (data && data.seconds > 0) {
            showVideo(block);
        }
    });

    record.revealBlock = block;
}

function buildTapToPlayButton() {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'vimeo-video-3d-avatar-bg__tap-play';
    button.setAttribute('aria-label', 'Video abspielen');
    button.innerHTML = '<svg viewBox="0 0 64 64" aria-hidden="true">'
        + '<circle cx="32" cy="32" r="30"></circle>'
        + '<path d="M26 20.5c0-2.2 2.4-3.6 4.3-2.5l16.5 9.6c1.9 1.1 1.9 3.9 0 5L30.3 42.2c-1.9 1.1-4.3-.3-4.3-2.5V20.5z"></path>'
        + '</svg>';

    return button;
}

/*
  If a block is still not visibly playing a while after coming into view,
  autoplay was most likely refused rather than merely slow. Offer a manual
  play button so the block does not just sit there looking broken — a tap
  runs play() inside a real user gesture, which browsers allow even when
  they refused the programmatic attempt.
*/
function scheduleTapFallback(record) {
    if (!record.revealBlock || record.tapButton) {
        return;
    }

    setTimeout(function () {
        if (!record.inView || record.revealBlock.classList.contains('video-is-playing')) {
            return;
        }

        var button = buildTapToPlayButton();

        button.addEventListener('click', function () {
            play(record);
            button.remove();
            record.tapButton = null;
        });

        record.revealBlock.appendChild(button);
        record.tapButton = button;
    }, tapFallbackDelay);
}

function revealIfPlaying(record) {
    if (!record.revealBlock || record.type !== 'vimeo') {
        return;
    }

    record.player.getCurrentTime().then(function (seconds) {
        if (seconds > 0) {
            showVideo(record.revealBlock);
        }
    }).catch(function () {
        /*
          If Vimeo does not respond, keep the fallback image visible.
        */
    });
}

/*
  Set while the mobile menu drawer is open (see the 'it:mobile-menu-toggle'
  listener in initBackgroundVideo). play() no-ops during that window so
  nothing already in view gets restarted by the stall check or a
  visibility/pageshow event while the drawer is up.
*/
var mobileMenuOpen = false;

function play(record) {
    if (mobileMenuOpen) {
        return;
    }

    if (record.type === 'vimeo') {
        record.player.play().catch(function () {
            /*
              Autoplay can be refused (low power mode, for example).
              The fallback image stays visible in that case.
            */
        });
        return;
    }

    var played = record.video.play();

    if (played && played.catch) {
        played.catch(function () {});
    }
}

function pause(record) {
    if (record.type === 'vimeo') {
        record.player.pause().catch(function () {});
        return;
    }

    record.video.pause();
}

/*
  Register one Elementor background video container.
  Returns silently if Elementor has not inserted the player yet.
  The MutationObserver below will try again later.
*/
function registerContainer(container) {
    if (seenContainers.has(container)) {
        return;
    }

    var target = container.parentElement || container;
    var iframe = container.querySelector('iframe');
    var hosted = container.querySelector('video');
    var record = null;

    if (iframe && window.Vimeo && window.Vimeo.Player) {
        try {
            record = {
                container: container,
                type: 'vimeo',
                player: new Vimeo.Player(iframe)
            };
        } catch (e) {
            /*
              If Vimeo initialization fails, leave the block alone.
              The fallback image remains visible.
            */
            return;
        }
    } else if (hosted) {
        record = {
            container: container,
            type: 'hosted',
            video: hosted
        };
    } else {
        return;
    }

    record.inView = false;
    record.lastTime = -1;
    record.stalls = 0;
    record.revealBlock = null;
    record.tapButton = null;

    seenContainers.add(container);
    videos.set(target, record);

    bindReveal(record);

    if (viewportObserver) {
        viewportObserver.observe(target);
    } else {
        /*
          Without IntersectionObserver we cannot tell what is on screen,
          so we leave Elementor's own autoplay untouched.
        */
        record.inView = true;
        revealIfPlaying(record);
        scheduleTapFallback(record);
    }
}

function registerAllContainers() {
    document.querySelectorAll(videoContainerSelector).forEach(registerContainer);
}

function handleIntersections(observerRecords) {
    observerRecords.forEach(function (observerRecord) {
        var record = videos.get(observerRecord.target);

        if (!record) {
            return;
        }

        record.inView = observerRecord.isIntersecting;

        if (!record.inView) {
            pause(record);

            if (record.tapButton) {
                record.tapButton.remove();
                record.tapButton = null;
            }

            return;
        }

        record.lastTime = -1;
        record.stalls = 0;
        play(record);
        revealIfPlaying(record);
        scheduleTapFallback(record);
    });
}

function resumeVisible() {
    videos.forEach(function (record) {
        if (record.inView) {
            play(record);
        }
    });
}

function pauseAllInView() {
    videos.forEach(function (record) {
        if (record.inView) {
            pause(record);
        }
    });
}

/*
  A suspended video keeps reporting itself as playing while its time
  stops advancing. Detect that and restart it, first with a plain play(),
  then by seeking back to the start.
*/
function checkForStalls() {
    if (document.hidden) {
        return;
    }

    videos.forEach(function (record) {
        if (!record.inView || record.type !== 'vimeo') {
            return;
        }

        record.player.getCurrentTime().then(function (seconds) {
            if (seconds !== record.lastTime) {
                record.stalls = 0;
                record.lastTime = seconds;
                return;
            }

            record.stalls += 1;
            record.lastTime = seconds;

            if (record.stalls === 1) {
                play(record);
                return;
            }

            record.stalls = 0;

            record.player.setCurrentTime(0).then(function () {
                play(record);
            }).catch(function () {
                play(record);
            });
        }).catch(function () {});
    });
}

export function initBackgroundVideo() {
    loadVimeoApi(function () {
        if (window.IntersectionObserver) {
            viewportObserver = new IntersectionObserver(handleIntersections, {
                rootMargin: preloadMargin,
                threshold: 0
            });
        }

        registerAllContainers();

        /*
          Elementor may insert players after DOMContentLoaded.
          This observer watches for later insertion and registers again.
        */
        var domObserver = new MutationObserver(function () {
            registerAllContainers();
        });

        domObserver.observe(document.body, {
            childList: true,
            subtree: true
        });

        /*
          Coming back from another tab, or from the back/forward cache,
          can leave playback suspended.
        */
        document.addEventListener('visibilitychange', function () {
            if (!document.hidden) {
                resumeVisible();
            }
        });

        window.addEventListener('pageshow', resumeVisible);

        document.addEventListener('it:mobile-menu-toggle', function (event) {
            mobileMenuOpen = !!(event.detail && event.detail.open);

            if (mobileMenuOpen) {
                pauseAllInView();
            } else {
                resumeVisible();
            }
        });

        if (viewportObserver && !stallTimer) {
            stallTimer = setInterval(checkForStalls, stallCheckInterval);
        }
    });
}
